import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModal from '../../../components/modal/GenericModal';
import SpinnerLoading from '../../../components/pageComponents/SpinnerLoading';
import { AuthContext } from '../../../hooks/context/authContext';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { downloadFile } from '../../../consumers/backendConsumer';
import {
    getFormularioConvocatoriaAPI,
    aplicarConvocatoriaAPI,
    getTPMotivosTrasladoAPI,
    getTPSedesInteresAPI,
    getEspecialidadSeleccionAPI,
    listarArchivosApoyoAPI,
} from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// Ficha de postulación (HU-TR-03 / HU-PR-03): un colaborador postula a una
// convocatoria publicada en la que forma parte de la población objetivo.
//
// 1) GET GetFormularioConvocatoria/{id}: cabecera, lineamientos y declaraciones.
// 2) El colaborador completa lo que pide su tipo de proceso y acepta las
//    declaraciones configuradas por GDH.
// 3) Submit → POST TrasladosPromociones/AplicarConvocatoria.
//
// Qué se pide según el tipo (es la única diferencia entre las dos vistas):
//
//   TRASLADO  (CA-08)  ¿Por qué deseas acceder a este traslado? — desplegable de
//                      DataType Discriminator='MotivoTraslado'. Si elige "Otros"
//                      se abre una caja de texto y el detalle se guarda en
//                      TP_Postulante.OtroMotivo.
//   PROMOCIÓN (CA-10)  Curso / nivel que dicta actualmente — un solo desplegable
//                      contra dbo.MatrizEspecialidadSeleccion (01/10/2026; mismo campo que ya usa el módulo viejo de
//                      Traslados en DetallePostularConvocatoria.jsx). Reemplaza a
//                      los 2 desplegables independientes de Curso/Nivel
//                      (DataType Discriminator='CR_Curso'/'CR_Nivel').
//
// En los dos casos: de 1 a 3 sedes de preferencia, distintas entre sí
// (CA-09/CA-11 en traslado, CA-12/CA-14 en promoción). Van a TP_SedeInteres.
//
// Lo que NO va acá: las preguntas abiertas/cerradas de la convocatoria. Esas son
// el "formato Director" que GDH configura en HU-TR-01 CA-13/CA-14, y las responde
// el Director en HU-TR-05 / HU-PR-04 — no el postulante.
//
// Pendiente de la HU (no implementado todavía): el apartado "Confirmación de
// datos" (CA-06/CA-07) con los datos corporativos de solo lectura.
//
// Archivos de apoyo (CA-05): se listan con ListarArchivosApoyo/{id} — mismo
// endpoint que ya usa GDH en NuevaConvocatoriaMasivo.jsx (Intranet.RestAPI los
// resuelve contra su propia dbo.Archivo, no contra Java), habilitado tambien
// para el postulante (UseCaseId.MisTrasladosYPromociones ya esta en la lista de
// UseCasesTrasladosPromociones del controller). Solo lectura/descarga aca: sin
// subir ni eliminar.
// ──────────────────────────────────────────────────────────────────────────

const MAX_SEDES = 3;
const CAMPOS_SEDE = ['IdSede1', 'IdSede2', 'IdSede3'];

// El detalle libre se habilita cuando el motivo elegido es "Otros". Se compara por
// texto porque el id de DataType no es fijo (es IDENTITY, cambia entre ambientes).
const esMotivoOtros = (motivos, idMotivo) => {
    const elegido = motivos.find(o => String(o.Value) === String(idMotivo));
    return /^otro/i.test(elegido?.Text ?? '');
};

const PostularConvocatoriaModal = ({ toggleModal, refreshPage, row }) => {
    const { state } = useContext(AuthContext);
    const [executeGet] = useGetRequest();
    const [managePostRequest] = useManagePostRequest();

    const [formulario, setFormulario] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [motivos, setMotivos] = useState([]);
    const [sedes, setSedes] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    // CA-05: archivos de apoyo de la convocatoria, solo lectura/descarga.
    const [archivosApoyo, setArchivosApoyo] = useState([]);

    // aceptadas: { [IdConvocatoriaDeclaracion]: {…} } con las declaraciones marcadas
    const [aceptadas, setAceptadas] = useState({});
    const [formValues, setFormValues] = useState({
        IdMotivoTraslado: '',
        OtroMotivo: '',
        IdMatrizEspecialidadSeleccion: '',
        IdSede1: '',
        IdSede2: '',
        IdSede3: '',
    });

    const esTraslado = formulario?.TipoProceso !== 'P';

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const respuesta = await executeGet(getFormularioConvocatoriaAPI(row?.idConvocatoria));
                const data = respuesta?.data;
                if (!data) return;
                setFormulario(data);

                // El catálogo de especialidad y el de motivo solo se piden para el
                // tipo que los usa; las sedes y los archivos de apoyo siempre.
                const promesas = [
                    executeGet(getTPSedesInteresAPI()),
                    executeGet(listarArchivosApoyoAPI(row?.idConvocatoria)),
                ];
                promesas.push(data.TipoProceso === 'P'
                    ? executeGet(getEspecialidadSeleccionAPI())
                    : executeGet(getTPMotivosTrasladoAPI()));

                const [resSedes, resArchivos, resTipo] = await Promise.all(promesas);
                setSedes(resSedes?.data ?? []);
                setArchivosApoyo(resArchivos?.data ?? []);
                if (data.TipoProceso === 'P') {
                    setEspecialidades(resTipo?.data ?? []);
                } else {
                    setMotivos(resTipo?.data ?? []);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row?.idConvocatoria]);

    const handleDescargarArchivoApoyo = (archivo) => {
        downloadFile(state.token, archivo.Path, archivo.OriginalFileName);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(lastData => {
            const next = { ...lastData, [name]: value };
            // Llenado secuencial de sedes de preferencia: si se vacía una, se
            // limpian en cascada las siguientes para no dejar huecos (evita que
            // el orden 1/2/3 se pierda al guardar/releer TP_SedeInteres).
            const idxCampo = CAMPOS_SEDE.indexOf(name);
            if (idxCampo !== -1 && !value) {
                CAMPOS_SEDE.slice(idxCampo + 1).forEach(campoSiguiente => {
                    next[campoSiguiente] = '';
                });
            }
            return next;
        });
    };

    const handleToggleDeclaracion = (id, titulo, texto, checked) => {
        setAceptadas(prev => {
            const next = { ...prev };
            if (checked) {
                next[id] = { IdConvocatoriaDeclaracion: id, TituloAceptado: titulo, TextoAceptado: texto };
            } else {
                delete next[id];
            }
            return next;
        });
    };

    // Los ids elegidos, en orden y sin los vacios.
    const sedesElegidas = CAMPOS_SEDE
        .map(campo => formValues[campo])
        .filter(valor => valor !== '' && valor != null);

    // CA-10 (HU-TR-03): la sede actual del colaborador no puede elegirse como
    // preferencia — pero SOLO en Traslado. En Promoción (HU-PR-03) sí puede
    // promocionar en su propia sede, no aplica esta exclusión. IdSedeActual lo
    // agrega el BFF .NET (no viene de Java) a partir de Empleado.IdSede.
    // CA-11 / CA-14: una sede no puede repetirse. Se resuelve escondiendo en cada
    // desplegable las que ya se eligieron en los otros, así el estado inválido no
    // se puede armar desde la UI (igual se valida en Java).
    const opcionesSede = (campo) => sedes.filter(o =>
        (!esTraslado || String(o.Value) !== String(formulario?.IdSedeActual))
        && (String(o.Value) === String(formValues[campo])
            || !sedesElegidas.some(elegida => String(elegida) === String(o.Value))));

    const validar = () => {
        if (sedesElegidas.length === 0) {
            return 'Debes seleccionar al menos una sede de preferencia.';
        }
        if (esTraslado) {
            if (!formValues.IdMotivoTraslado) {
                return 'Debes indicar por qué deseas acceder a este traslado.';
            }
            if (esMotivoOtros(motivos, formValues.IdMotivoTraslado) && !formValues.OtroMotivo?.trim()) {
                return 'Debes detallar el motivo del traslado.';
            }
        } else {
            if (!formValues.IdMatrizEspecialidadSeleccion) {
                return 'Debes indicar el curso y el nivel que dictas actualmente.';
            }
        }
        // CA-16: todas las declaraciones juradas son obligatorias, sin excepción —
        // no alcanza con aceptar al menos una.
        const declaraciones = formulario?.Declaraciones ?? [];
        const faltanDeclaraciones = declaraciones.some(
            d => !aceptadas[d.IdConvocatoriaDeclaracion]
        );
        if (faltanDeclaraciones) {
            return 'Debes aceptar todas las declaraciones juradas para postular.';
        }
        return null;
    };

    const getPayload = () => {
        const base = {
            IdConvocatoria: Number(formulario.IdConvocatoria),
            SedesInteres: sedesElegidas.map(Number),
            Declaraciones: Object.values(aceptadas),
        };

        // El IdEmpleado NO se manda: lo resuelve el BFF con la sesión
        // (TrasladosPromocionesService.AplicarConvocatoria). Cada tipo de proceso
        // envía solo sus campos, así el otro queda en null en TP_Postulante.
        if (esTraslado) {
            return {
                ...base,
                IdMotivoTraslado: Number(formValues.IdMotivoTraslado),
                OtroMotivo: esMotivoOtros(motivos, formValues.IdMotivoTraslado)
                    ? formValues.OtroMotivo.trim()
                    : null,
            };
        }
        return {
            ...base,
            IdMatrizEspecialidadSeleccion: Number(formValues.IdMatrizEspecialidadSeleccion),
        };
    };

    const handleSubmit = () => {
        if (isSubmitting || !formulario) return;

        const error = validar();
        if (error) {
            toast.error(error);
            return;
        }

        setIsSubmitting(true);
        managePostRequest(
            aplicarConvocatoriaAPI(),
            getPayload(),
            () => {
                setIsSubmitting(false);
                toggleModal();
                refreshPage && refreshPage();
            },
            true,
            () => setIsSubmitting(false),
            true
        );
    };

    const declaraciones = formulario?.Declaraciones ?? [];
    // CA-04: la cabecera identifica la campaña a la que se postula.
    const titulo = formulario?.NombreConvocatoria
        ? `Postulación – ${formulario.NombreConvocatoria}`
        : 'Postulación';

    return (
        <GenericModal showModal={toggleModal} titulo={titulo} width="700px">
            <div className="modal-body">
                {isLoading ? (
                    <div className="d-flex justify-content-center py-4">
                        <SpinnerLoading />
                    </div>
                ) : !formulario ? (
                    <div className="alert alert-warning">No se pudo cargar la convocatoria.</div>
                ) : (
                    <React.Fragment>
                        <div className="form-group">
                            <label className="modal-label mb-0">Tipo</label>
                            <input type="text" className="form-control"
                                value={`${esTraslado ? 'Traslado' : 'Promoción'} · ${formulario.TipoModalidad === 'M' ? 'Masivo' : 'Individual'}`}
                                disabled />
                        </div>
                        <div className="form-group">
                            <label className="modal-label mb-0">Responsable</label>
                            <input type="text" className="form-control" value={formulario.NombreEmpleadoResponsable ?? ""} disabled />
                        </div>

                        {formulario.Lineamientos && (
                            <div className="form-group">
                                <label className="modal-label mb-0">Lineamientos</label>
                                {/* CA-06 de la HU de creación: los lineamientos se guardan con
                                    formato (HTML). Mismo patrón de render que Cuponera. */}
                                <div className="form-control h-auto"
                                    style={{ minHeight: "3rem", maxHeight: "14rem", overflowY: "auto", backgroundColor: "#e9ecef" }}
                                    dangerouslySetInnerHTML={{ __html: formulario.Lineamientos }} />
                            </div>
                        )}

                        {/* CA-05: archivos de apoyo (tutoriales, flyers, etc.) adjuntados
                            por GDH al crear la convocatoria — solo lectura/descarga. */}
                        {archivosApoyo.length > 0 && (
                            <div className="form-group">
                                <label className="modal-label mb-0">Archivos de apoyo</label>
                                {archivosApoyo.map((archivo) => (
                                    <div key={archivo.IdArchivo}>
                                        <FontAwesomeIcon icon="paperclip" className="mr-1" />
                                        <a href="#!" onClick={(e) => { e.preventDefault(); handleDescargarArchivoApoyo(archivo); }}>
                                            {archivo.OriginalFileName}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        )}

                        {esTraslado ? (
                            <React.Fragment>
                                <div className="form-group">
                                    <label className="modal-label mb-0 required">
                                        ¿Por qué deseas acceder a este traslado?
                                    </label>
                                    <select className="form-control" name="IdMotivoTraslado"
                                        value={formValues.IdMotivoTraslado}
                                        onChange={handleChange}>
                                        <option value="">Seleccione...</option>
                                        {motivos.map(o => (
                                            <option key={o.Value} value={o.Value}>{o.Text}</option>
                                        ))}
                                    </select>
                                </div>

                                {esMotivoOtros(motivos, formValues.IdMotivoTraslado) && (
                                    <div className="form-group">
                                        <label className="modal-label mb-0 required">Detalla el motivo</label>
                                        <textarea className="form-control" name="OtroMotivo" rows={3}
                                            maxLength={400}
                                            value={formValues.OtroMotivo}
                                            onChange={handleChange}
                                            placeholder="Cuéntanos el motivo de tu solicitud..." />
                                    </div>
                                )}
                            </React.Fragment>
                        ) : (
                            <div className="form-group">
                                <label className="modal-label mb-0 required">
                                    Curso / nivel que podria dictar
                                </label>
                                <select className="form-control" name="IdMatrizEspecialidadSeleccion"
                                    value={formValues.IdMatrizEspecialidadSeleccion}
                                    onChange={handleChange}>
                                    <option value="">Seleccione...</option>
                                    {especialidades.map(o => (
                                        <option key={o.Value} value={o.Value}>{o.Text}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="form-group">
                            <label className="modal-label mb-0 required">
                                Sedes de preferencia (hasta {MAX_SEDES})
                            </label>
                            <small className="d-block text-muted mb-1">
                                La primera es obligatoria.{esTraslado && ' No puedes elegir tu sede actual.'}
                            </small>
                            {CAMPOS_SEDE.map((campo, index) => (
                                <select key={campo} className="form-control mb-2" name={campo}
                                    value={formValues[campo]}
                                    onChange={handleChange}
                                    disabled={index > 0 && !formValues[CAMPOS_SEDE[index - 1]]}>
                                    <option value="">
                                        {index === 0 ? 'Sede de preferencia 1...' : `Sede de preferencia ${index + 1} (opcional)...`}
                                    </option>
                                    {opcionesSede(campo).map(o => (
                                        <option key={o.Value} value={o.Value}>{o.Text}</option>
                                    ))}
                                </select>
                            ))}
                        </div>

                        {declaraciones.length > 0 && (
                            <div className="form-group">
                                <label className="modal-label mb-0">Declaraciones juradas</label>
                                {declaraciones.map((d) => (
                                    <div key={d.IdConvocatoriaDeclaracion} className="custom-control custom-checkbox mb-2">
                                        <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            id={`decl-${d.IdConvocatoriaDeclaracion}`}
                                            checked={!!aceptadas[d.IdConvocatoriaDeclaracion]}
                                            onChange={(e) => handleToggleDeclaracion(
                                                d.IdConvocatoriaDeclaracion,
                                                d.TituloDeclaracion,
                                                d.DetalleDeclaracion,
                                                e.target.checked
                                            )}
                                        />
                                        <label className="custom-control-label" htmlFor={`decl-${d.IdConvocatoriaDeclaracion}`}>
                                            <strong>{d.TituloDeclaracion}</strong>
                                            {d.DetalleDeclaracion && <div className="text-muted">{d.DetalleDeclaracion}</div>}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="modal-footer pl-0">
                            <div />
                            <button
                                type="button"
                                className="generic-button-buscador generic-button-hover-verde green-innova-background"
                                onClick={handleSubmit}
                                disabled={isSubmitting}>
                                Enviar postulación
                            </button>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </GenericModal>
    );
};

export default PostularConvocatoriaModal;
